import React, { useState } from "react";
import PropTypes from "prop-types";

import Link from "core/Link";
import Checkbox from "core/Checkbox";
import TruncateText from "core/TruncateText";
import IndividualUploadLoader from "./IndividualUploadLoader";

import { ContentContainer, AssetContainer } from "core/UploadsGrid/styles";
import {
    SelectedCheckboxContainer,
    HoverBodyText,
    FilePreviewContainer,
    Image,
    FooterContentContainer
} from "./styles";

import { useSelect } from "../PhotoUpload/hooks/useSelect";
import { useEditPhotoUpload } from "../PhotoUpload/Edit/EditPhotoUploadContext";
import { usePhotoUploadsInDropzone } from "../PhotoUploadCore/hooks";

const ItemContent = ({
    id,
    name,
    preview,
    gridIndex,
    handleItemContentChange
}) => {
    const { openEditModal } = useEditPhotoUpload();
    const [isHovered, updateHovered] = useState(false);
    const [isImageProcessing, updateImageProcessing] = useState(true);

    const { uploadCount } = usePhotoUploadsInDropzone();

    const {
        quantityChecked,
        isChecked,
        toggleChecked,
        checkItems
    } = useSelect();

    const isCheckboxChecked = isChecked[id] || false;

    const checkboxValue = isCheckboxChecked ? `${name} is selected` : name;

    const editLinkDisabled = quantityChecked > 1;

    const editAriaLabel = editLinkDisabled
        ? "Edit link disabled - please use Edit button above images"
        : `Edit file name "${name}"`;

    const handleCheckboxClick = () => {
        toggleChecked([id]);
    };

    const handleMouseEnter = () => {
        updateHovered(true);
    };

    const handleMouseLeave = () => {
        updateHovered(false);
    };

    const handleImageOnLoad = () => {
        updateImageProcessing(false);
    };

    const handleEditLinkClick = () => {
        checkItems([id]);
        !editLinkDisabled && openEditModal();
    };

    return (
        <FilePreviewContainer isImageProcessing={isImageProcessing}>
            <ContentContainer>
                <AssetContainer
                    onMouseEnter={handleMouseEnter} // for mouse
                    onMouseLeave={handleMouseLeave} // for mouse
                    onFocus={handleMouseEnter} // for keyboard
                    onBlur={handleMouseLeave} // for keyboard
                >
                    <SelectedCheckboxContainer
                        isCheckboxSelected={isCheckboxChecked}
                        isHovered={isHovered}
                        title={name} // fileName shows on mouse hover due to truncation in UI
                    >
                        <Checkbox
                            ariaLabel={name}
                            label=""
                            isChecked={isCheckboxChecked}
                            value={checkboxValue}
                            onChange={e =>
                                handleItemContentChange(e, gridIndex, id)
                            }
                            onClick={handleCheckboxClick}
                        />

                        <HoverBodyText size="xs" type="div">
                            {gridIndex + 1} of {uploadCount}
                        </HoverBodyText>

                        <HoverBodyText size="xs" type="div">
                            <TruncateText charLimit={15}>{name}</TruncateText>
                        </HoverBodyText>
                    </SelectedCheckboxContainer>

                    {isImageProcessing && <IndividualUploadLoader />}

                    <Image
                        data-testid={`added-file-image-${gridIndex}`}
                        src={preview}
                        alt={`File name ${name}`}
                        onLoad={handleImageOnLoad}
                    />
                </AssetContainer>

                {!isImageProcessing && (
                    <FooterContentContainer
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Link
                            isButton
                            styleType="primary"
                            size="md"
                            aria-label={editAriaLabel}
                            aria-disabled={editLinkDisabled}
                            isDisabled={editLinkDisabled}
                            onClick={handleEditLinkClick}
                        >
                            Edit
                        </Link>
                    </FooterContentContainer>
                )}
            </ContentContainer>
        </FilePreviewContainer>
    );
};

ItemContent.propTypes = {
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    preview: PropTypes.string.isRequired,
    gridIndex: PropTypes.number.isRequired,
    handleItemContentChange: PropTypes.func.isRequired
};

export default ItemContent;
